import React, { useState, useEffect } from 'react';
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "react-data-table-component-extensions/dist/index.css";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AnalyticsApiService from '../../ApiServices/AnalyticsApiService/AnalyticsApiService';
import { useAuth } from '../Auth/AuthContext';
import AddAnalyticsForm from './AddAnalyticsForm';
import Datepicker from "react-tailwindcss-datepicker";
import { Calendar, User, Globe, Layout, MapPin, Eye, MousePointer, Percent, DollarSign, CreditCard, BarChart3, RefreshCcw, TrendingUp, TrendingDown, MoreVertical } from "lucide-react";
import EditAnalyticsForm from './EditAnalyticsForm';

const AnalyticsTable = () => {
  const [editData, setEditData] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [editModalIsOpen, setEditModalIsOpen] = useState(false);
  const [selectedPublisher, setSelectedPublisher] = useState("");
  const [selectedWebsite, setSelectedWebsite] = useState("");
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [analytics, setAnalytics] = useState([]);
  const Auth = useAuth();
  const user = Auth.authState.user;
  const role = Auth.authState.roles;
  const admin = role === "Admin";
  const uniquePublishers = [...new Set(analytics.map(entry => entry.publisher))];
  const uniqueWebsites = [...new Set(analytics.map(entry => entry.website))];
  const fetchAnalytics = async () => {
    try {
      const response = admin ? await AnalyticsApiService.getAll() : await AnalyticsApiService.GetUserAnalytics(user.id);
      setAnalytics(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch analytics.');
    }
  };
  // Refresh analytics data when a new entry is submitted
  const handleAnalyticsSubmit = () => {
    fetchAnalytics(); // Call the function to refresh data
  };
  useEffect(() => {
    if (user && user.id) {
      fetchAnalytics();
    }
  }, [user]);



  const filteredAnalytics = analytics.filter(entry => {
    const entryDate = new Date(entry.date);
    const startDate = dateRange.startDate ? new Date(dateRange.startDate) : null;
    const endDate = dateRange.endDate ? new Date(dateRange.endDate) : null;

    // Normalize all dates to midnight (00:00:00)
    if (startDate) startDate.setHours(0, 0, 0, 0);
    if (endDate) endDate.setHours(23, 59, 59, 999); // Extend end date to the end of the day
    entryDate.setHours(0, 0, 0, 0);

    const isWithinRange =
      (!startDate || entryDate >= startDate) &&
      (!endDate || entryDate <= endDate);

    return (
      (selectedPublisher ? entry.publisher === selectedPublisher : true) &&
      (selectedWebsite ? entry.website === selectedWebsite : true) &&
      isWithinRange
    );
  });
  const handleResetFilters = () => {
    setTimeout(() => {
      setSelectedPublisher("");
      setSelectedWebsite("");
      setDateRange({ startDate: null, endDate: null });
    }, 800); // Simulate a delay
  };

  // Modify filter change functions
  const handleFilterChange = (setter, value) => {
    setTimeout(() => {
      setter(value);
    }, 800);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      try {
        await AnalyticsApiService.deleteAnalytics(id);
        setAnalytics(analytics.filter(entry => entry.id !== id));
        toast.success('Deleted successfully!');
      } catch (error) {
        toast.error("Error deleting analytics");
      }
    }
  };
  const handleEdit = (row) => {
    setEditData(row);
    setEditModalIsOpen(true);
  };
  const columns = [
    { name: "No", selector: (_, index) => index + 1, width: "50px", sortable: true },
    { name: "Date", selector: row => new Date(row.date).toISOString().split("T")[0], sortable: true, icon: <Calendar size={16} /> },
    { name: "Publisher", selector: row => row.publisher, width: "150px", sortable: true, icon: <User size={16} /> },
    { name: "Site", selector: row => row.website, sortable: true, width: "180px", icon: <Globe size={16} /> },
    { name: "Adunits", selector: row => row.adUnit, sortable: true, width: "190px", icon: <Layout size={16} /> },
    { name: "Country", selector: row => row.country, sortable: true, width: "120px", icon: <MapPin size={16} /> },
    { name: "Impressions", selector: row => row.adExchangeImpressions, sortable: true, icon: <Eye size={16} /> },
    { name: "Clicks", selector: row => row.adExchangeClicks, sortable: true, icon: <MousePointer size={16} /> },
    { name: "CTR (%)", selector: row => row.adExchangeCTR, sortable: true, icon: <Percent size={16} /> },
    { name: "Revenue ($)", selector: row => row.adExchangeRevenue, sortable: true, icon: <DollarSign size={16} /> },
    { name: "Payout", selector: row => row.adExchangePayout, sortable: true, icon: <CreditCard size={16} /> },
    { name: "eCPM ($)", selector: row => row.adExchangeAvgECPM, sortable: true, icon: <BarChart3 size={16} /> },
    { name: "Requests", selector: row => row.adExchangeAdRequests, sortable: true, icon: <RefreshCcw size={16} /> },
    { name: "Match Rate (%)", selector: row => row.adExchangeMatchRate, sortable: true, icon: <TrendingUp size={16} /> },
    { name: "Fill Rate (%)", selector: row => row.adExchangeFillRate, sortable: true, icon: <TrendingDown size={16} /> }
  ];
  // Conditionally add "Actions" column if the user is an admin
  if (admin) {
    columns.push({
      name: "Actions",
      cell: (row) => (
        <div className="flex gap-2">
          <button onClick={() => handleEdit(row)} className="text-blue-500 hover:underline">
            Edit
          </button>
          <button onClick={() => handleDelete(row.id)} className="text-red-500 hover:underline">
            Delete
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    });
  }
  const customStyles = {
    headRow: {
      style: {
        background: "linear-gradient(to right, #9333EA, #EC4899)",
      },
    },
    headCells: {
      style: {
        color: "#fff", // Equivalent to text-white
        fontWeight: "bold",
      },
    },
  };
  // Define columns and data
  const tableData = {
    columns: columns,
    data: filteredAnalytics,
  };

  // ✅ Calculate Totals for Footer
  const totalImpressions = filteredAnalytics.reduce((sum, row) => sum + (row.adExchangeImpressions || 0), 0);
  const totalClicks = filteredAnalytics.reduce((sum, row) => sum + (row.adExchangeClicks || 0), 0);
  const totalCTR = (totalImpressions > 0) ? ((totalClicks / totalImpressions) * 100).toFixed(2) : 0;
  const totalRevenue = filteredAnalytics.reduce((sum, row) => sum + (row.adExchangeRevenue || 0), 0).toFixed(2);
  const totalPayout = filteredAnalytics.reduce((sum, row) => sum + (row.adExchangePayout || 0), 0).toFixed(2);
  const totalRequests = filteredAnalytics.reduce((sum, row) => sum + (row.adExchangeAdRequests || 0), 0);
  const totalFillRate = (filteredAnalytics.length > 0) ? (filteredAnalytics.reduce((sum, row) => sum + (row.adExchangeFillRate || 0), 0) / filteredAnalytics.length).toFixed(2) : 0;
  var sumObj = {
    totalImpressions, totalClicks, totalCTR, totalRevenue, totalPayout, totalRequests, totalFillRate
  }
  const exportToExcel = (data) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Analytics Data");

    // Convert to buffer and create a Blob
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });

    // Save file
    saveAs(dataBlob, "Analytics_Data.xlsx");
  };
  const exportSummaryToExcel = (sum) => {
    const sumRow = {
      No: "Total",
      Date: "",
      Publisher: "",
      Site: "",
      Adunits: "",
      Country: "",
      Impressions: sum.totalImpressions,
      Clicks: sum.totalClicks,
      "CTR (%)": sum.totalCTR,
      "Revenue ($)": sum.totalRevenue,
      Payout: sum.totalPayout,
      "eCPM ($)": "",
      Requests: sum.totalRequests,
      "Match Rate (%)": "",
      "Fill Rate (%)": sum.totalFillRate,
    };
    const worksheet = XLSX.utils.json_to_sheet(sumRow);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Analytics Summary");

    // Convert to buffer and create a Blob
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });

    // Save file
    saveAs(dataBlob, "Analytics_Summary.xlsx");
  };
  const handleUpdateAnalytics = async (updatedData) => {
    try {
      debugger
      const id = updatedData.id;
      const payload = { ...updatedData };
  
      // Optional: Remove ID from payload if backend doesn't need it
      delete payload.id;
      payload.date = updatedData.date.toISOString();
      const response = await  AnalyticsApiService.updateAnalytics(id, payload);
      toast.success("Update Analytics");
  
      // Optionally refresh data
      fetchAnalytics(); // or whatever function reloads your analytics
    } catch (error) {
      console.error('Update failed:', error);
    }
  };
  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <ToastContainer />
      {/* Filters Section */}
      <div className="bg-white shadow-lg rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {/* Publisher Filter */}
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2">Publisher</label>
            <select
              className="w-full border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500"
              value={selectedPublisher}
              onChange={(e) => handleFilterChange(setSelectedPublisher, e.target.value)}
            >
              <option value="">All Publishers</option>
              {uniquePublishers.map((publisher) => (
                <option key={publisher} value={publisher}>{publisher}</option>
              ))}
            </select>
          </div>

          {/* Website Filter */}
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2">Website</label>
            <select
              className="w-full border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500"
              value={selectedWebsite}
              onChange={(e) => handleFilterChange(setSelectedWebsite, e.target.value)}
            >
              <option value="">All Websites</option>
              {uniqueWebsites.map((website) => (
                <option key={website} value={website}>{website}</option>
              ))}
            </select>
          </div>

          {/* Date Range Picker */}
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2">Date Range</label>
            <Datepicker
              value={dateRange}
              onChange={(value) => handleFilterChange(setDateRange, value)}
              placeholder="Select Date Range"
              showShortcuts={true}
              inputClassName="w-full border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Reset Filters Button */}
          <div className="flex items-end">
            <button
              onClick={handleResetFilters}
              className="w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition"
            >
              Reset Filters
            </button>
          </div>

          {/* Add Analytics Button */}

          <div className="flex items-end">
            {admin ? <button
              onClick={() => setModalIsOpen(true)}
              className="w-full bg-blue-500 text-white px-5 py-2 rounded-lg hover:bg-blue-600 transition"
            >
              Add Analytics
            </button> : null}

          </div>
        </div>
      </div>
      <div className="bg-white shadow-lg rounded-lg p-6">
        <button className='px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition duration-200'
          onClick={() => exportToExcel(filteredAnalytics)}>Export to excel</button>
        {/* <button className='px-4 py-2 bg-yellow-600 text-white rounded-lg shadow-md hover:bg-yellow-700 transition duration-200'
          onClick={() => exportSummaryToExcel(sumObj)}>Export Summary</button> */}
        <DataTable
          title="Analytics Data"
          pagination
          highlightOnHover
          striped
          responsive
          className="min-w-full bg-white border-collapse"
          customStyles={customStyles}
          columns={columns}
          data={filteredAnalytics}
        />

        {/* ✅ Enhanced Footer UI */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-100 p-6 mt-4 border border-gray-300 rounded-lg shadow-md text-gray-800">
          <div className="flex flex-col">
            <span className="text-sm text-gray-500">Total Impressions</span>
            <span className="text-lg font-semibold">{totalImpressions}</span>
          </div>

          <div className="flex flex-col">
            <span className="text-sm text-gray-500">Total Clicks</span>
            <span className="text-lg font-semibold">{totalClicks}</span>
          </div>

          <div className="flex flex-col">
            <span className="text-sm text-gray-500">Total CTR</span>
            <span className="text-lg font-semibold">{totalCTR}%</span>
          </div>

          <div className="flex flex-col">
            <span className="text-sm text-gray-500">Total Revenue</span>
            <span className="text-lg font-semibold">${totalRevenue}</span>
          </div>

          <div className="flex flex-col">
            <span className="text-sm text-gray-500">Total Payout</span>
            <span className="text-lg font-semibold">${totalPayout}</span>
          </div>

          <div className="flex flex-col">
            <span className="text-sm text-gray-500">Total Requests</span>
            <span className="text-lg font-semibold">{totalRequests}</span>
          </div>

          <div className="flex flex-col">
            <span className="text-sm text-gray-500">Avg Fill Rate</span>
            <span className="text-lg font-semibold">{totalFillRate}%</span>
          </div>
        </div>

      </div>
      {modalIsOpen && (
        <AddAnalyticsForm
          isOpen={modalIsOpen}
          onRequestClose={() => setModalIsOpen(false)}
          onSubmit={handleAnalyticsSubmit}
        />
      )}

      {editModalIsOpen && (
        <EditAnalyticsForm
          data={editData}
          onClose={() => setEditModalIsOpen(false)}
          onSubmit={handleUpdateAnalytics}
        />
      )}
    </div>
  );
};

export default AnalyticsTable;
